import { format } from 'date-fns';
import { getIpeWorkspaceMode } from '@/utils/api';
import { normalizeWorkspaceMode } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import { resolveImageSpaceSourceKey } from '@/pages/ipe/workbench/constants/imageSpaceSourceOptions';
import { hasImageSpacePersistedUrl } from '@/pages/ipe/workbench/utils/imageSpaceDisplayUrl';
import { resolveImageRecordWorkspace } from '@/pages/ipe/workbench/utils/imageSpaceMeta';
import { userLocal } from '@/utils/ipeUserLocalState';
import { normalizeImageSpaceCorrelateUrl } from '@/utils/imageSpaceBatchCorrelate';

const IMAGE_SPACE_CACHE_KEY_PREFIX = 'ipe-image-space-cache-v2';
const LEGACY_FUNDING_IMAGE_SPACE_CACHE_KEY = 'funding-image-space-cache-v1';

function imageSpaceCacheKey(workspaceMode) {
  return `${IMAGE_SPACE_CACHE_KEY_PREFIX}-${normalizeWorkspaceMode(workspaceMode)}`;
}

/** 仅「图片空间」相关导航才主动拉列表；其它工具进页不预拉，打开选图弹窗时再请求 */
const IMAGE_SPACE_LOAD_NAV_IDS = new Set([
  'imageSpace',
  'spaceHub',
  'spaceCollection',
  'spaceFavorites',
  'spaceTrash',
]);

/**
 * 空间 Hub 内 Tab（含下载中心）。在这些导航间切换时保留已加载列表，
 * 避免「下载 → 下载中心 → 返回」触发整表重拉把本地多出的记录盖掉。
 */
const IMAGE_SPACE_HUB_NAV_IDS = new Set([
  'imageSpace',
  'spaceHub',
  'spaceCollection',
  'spaceFavorites',
  'spaceTrash',
  'downloadCenter',
]);

function parseImageSpaceDate(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const text = String(value).trim();
  if (!text) return null;
  const normalized = text.includes('T') ? text : text.replace(' ', 'T');
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function sanitizeImageSpaceRecords(rawImages) {
  const images = Array.isArray(rawImages) ? rawImages : [];
  const seenIds = new Set();
  const sanitized = images
    .filter((item) => {
      if (!item || item.id == null || item.id === '') return false;
      const id = String(item.id);
      if (seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    })
    .map((item) => {
      let createdAt = '';
      if (item.createdAt) {
        if (typeof item.createdAt === 'string' && item.createdAt.includes('T') && item.createdAt.includes('Z')) {
          try {
            const date = new Date(item.createdAt);
            createdAt = !Number.isNaN(date.getTime()) ? format(date, 'yyyy-MM-dd HH:mm') : item.createdAt;
          } catch {
            createdAt = item.createdAt;
          }
        } else {
          createdAt = item.createdAt;
        }
      } else if (item.uploadedAt) {
        if (item.uploadedAt instanceof Date) {
          createdAt = format(item.uploadedAt, 'yyyy-MM-dd HH:mm');
        } else if (typeof item.uploadedAt === 'string') {
          if (item.uploadedAt.includes('T') && (item.uploadedAt.includes('Z') || item.uploadedAt.includes('+'))) {
            try {
              const date = new Date(item.uploadedAt);
              createdAt = !Number.isNaN(date.getTime()) ? format(date, 'yyyy-MM-dd HH:mm') : item.uploadedAt;
            } catch {
              createdAt = item.uploadedAt;
            }
          } else {
            createdAt = item.uploadedAt;
          }
        } else {
          try {
            const date = new Date(item.uploadedAt);
            if (!Number.isNaN(date.getTime())) {
              createdAt = format(date, 'yyyy-MM-dd HH:mm');
            }
          } catch {
            /* ignore */
          }
        }
      }

      const usage = Number(item.usageCount ?? item.useCount ?? item.referenceCount ?? 0) || 0;
      const rawSz = item.size ?? item.fileSize ?? item.byteSize;
      const sizeNum = rawSz != null && rawSz !== '' && !Number.isNaN(Number(rawSz)) ? Number(rawSz) : null;
      const ownerId = item.ownerUserId
        ?? item.owner_user_id
        ?? item.ownerId
        ?? item.owner_id
        ?? item.createdByUserId
        ?? item.userId
        ?? item.user_id
        ?? '';
      const ownerName = item.ownerName || item.owner_name || item.createdByName || item.created_by_name || '';
      const createdBy = item.createdBy || item.created_by || item.creator || ownerName || '';
      const createdByName = item.createdByName || item.created_by_name || createdBy || ownerName || '';
      const workspaceMode = resolveImageRecordWorkspace(item);
      return {
        id: String(item.id),
        name: item.name || '',
        createdAt,
        width: Number(item.width) || null,
        height: Number(item.height) || null,
        size: sizeNum,
        url: item.url || '',
        viewUrl: item.viewUrl || item.storedViewUrl || '',
        displayUrl: item.displayUrl || item.viewUrl || item.storedUrlDisplay || '',
        storedViewUrl: item.storedViewUrl || '',
        storedUrlDisplay: item.storedUrlDisplay || '',
        dataUrl: item.dataUrl || null,
        source: item.source || item.sourceType || 'upload',
        sourceType: item.sourceType || '',
        category: item.category || '',
        fissionBatchId: item.fissionBatchId || '',
        taskId: item.taskId || item.sourceTaskId || '',
        batchId: item.batchId || item.materialBatchId || '',
        usageCount: usage,
        useCount: usage,
        referenceCount: usage,
        contentHash: item.contentHash || null,
        favorite: item.favorite === true || item.isFavorite === true,
        deletedAt: (() => {
          const raw = item.deletedAt;
          if (raw == null) return null;
          const text = String(raw).trim();
          if (!text || text === 'null' || text === 'undefined') return null;
          return text;
        })(),
        folderId: item.folderId ? String(item.folderId) : '',
        ownerId: ownerId ? String(ownerId) : '',
        ownerUserId: ownerId ? String(ownerId) : '',
        ownerName,
        createdBy,
        createdByName,
        workspaceMode,
        captureSpace: workspaceMode,
        teamId: item.teamId != null && item.teamId !== '' ? String(item.teamId) : '',
        teamShared: item.teamShared === true || String(item.teamShared) === 'true',
        teamSharedVia: item.teamSharedVia || '',
        teamSharedFolderId: item.teamSharedFolderId ? String(item.teamSharedFolderId) : '',
        ingestPending: item.ingestPending === true,
        ...(item.clientLocalId != null && String(item.clientLocalId).trim() !== ''
          && String(item.clientLocalId) !== String(item.id)
          ? { clientLocalId: String(item.clientLocalId) }
          : {}),
      };
    });

  return sortImageSpaceRecordsByCreatedAtDesc(sanitized);
}

/** 本地未入库项：入库失败 / 仅有本机预览，尚无受管 url */
function isLocalUnsyncedImageSpaceItem(item) {
  if (!item || item.id == null || item.id === '') return false;
  if (item.ingestPending === true) return true;
  return !hasImageSpacePersistedUrl(item);
}

function imageSpaceItemHasDisplayUrl(item) {
  if (!item || typeof item !== 'object') return false;
  if (hasImageSpacePersistedUrl(item)) return true;
  const view = typeof item.viewUrl === 'string' ? item.viewUrl.trim() : '';
  const display = typeof item.displayUrl === 'string' ? item.displayUrl.trim() : '';
  return Boolean(view || display);
}

function preferImageSpaceUrlFields(primary, fallback) {
  if (!primary) return fallback || null;
  if (!fallback) return primary;
  const take = (a, b) => {
    const av = typeof a === 'string' ? a.trim() : '';
    const bv = typeof b === 'string' ? b.trim() : '';
    return av || bv || '';
  };
  const merged = {
    ...fallback,
    ...primary,
    id: primary.id || fallback.id,
    clientLocalId: primary.clientLocalId || fallback.clientLocalId,
    url: take(primary.url, fallback.url),
    viewUrl: take(primary.viewUrl, fallback.viewUrl),
    displayUrl: take(primary.displayUrl, fallback.displayUrl),
    dataUrl: primary.dataUrl || fallback.dataUrl || null,
    name: (primary.name && String(primary.name).trim()) || fallback.name || '',
  };
  const mounted = imageSpaceItemHasDisplayUrl(merged) || hasImageSpacePersistedUrl(merged);
  merged.ingestPending = mounted
    ? false
    : (primary.ingestPending === true || fallback.ingestPending === true);
  if (!merged.ingestPending) {
    merged.dataUrl = null;
  }
  return merged;
}

function indexImageSpaceItemsForMerge(lists) {
  const byId = new Map();
  const byLocalId = new Map();
  const byUrl = new Map();
  const all = [];
  lists.forEach((list) => {
    (Array.isArray(list) ? list : []).forEach((item) => {
      if (!item || item.id == null || item.id === '') return;
      all.push(item);
      const id = String(item.id);
      if (!byId.has(id)) byId.set(id, item);
      const localId = item.clientLocalId != null && String(item.clientLocalId).trim() !== ''
        ? String(item.clientLocalId)
        : '';
      if (localId && !byLocalId.has(localId)) byLocalId.set(localId, item);
      const u = normalizeImageSpaceCorrelateUrl(item.url);
      if (u && !byUrl.has(u)) byUrl.set(u, item);
    });
  });
  return { byId, byLocalId, byUrl, all };
}

function findImageSpaceMergeMatch(item, index) {
  if (!item || item.id == null || item.id === '') return null;
  const id = String(item.id);
  if (index.byId.has(id)) return index.byId.get(id);
  const localId = item.clientLocalId != null && String(item.clientLocalId).trim() !== ''
    ? String(item.clientLocalId)
    : '';
  if (localId && index.byId.has(localId)) return index.byId.get(localId);
  if (localId && index.byLocalId.has(localId)) return index.byLocalId.get(localId);
  if (index.byLocalId.has(id)) return index.byLocalId.get(id);
  const u = normalizeImageSpaceCorrelateUrl(item.url);
  if (u && index.byUrl.has(u)) return index.byUrl.get(u);
  return null;
}

function sortImageSpaceRecordsByCreatedAtDesc(images) {
  return [...(Array.isArray(images) ? images : [])].sort((a, b) => {
    const timeA = a.createdAt || '';
    const timeB = b.createdAt || '';
    if (!timeA && !timeB) return 0;
    if (!timeA) return 1;
    if (!timeB) return -1;
    return new Date(timeB.replace(' ', 'T')) - new Date(timeA.replace(' ', 'T'));
  });
}

/**
 * T-A11 三键合并：serverId ∪ clientLocalId ∪ normalize(url)。
 * 刷新不得用「无 url 的服务端行」盖掉「本地已有 url/签名的同行」；
 * 仍保留未入库本地 pending（id 不在服务端且无持久化 url）。
 */
function mergeServerImageSpaceWithLocalPending(serverImages, ...localLists) {
  const server = Array.isArray(serverImages) ? serverImages : [];
  const localIndex = indexImageSpaceItemsForMerge(localLists);
  const matchedLocalKeys = new Set();
  const merged = [];

  for (const serverItem of server) {
    if (!serverItem || serverItem.id == null || serverItem.id === '') continue;
    const localMatch = findImageSpaceMergeMatch(serverItem, localIndex);
    if (!localMatch) {
      merged.push(serverItem);
      continue;
    }
    matchedLocalKeys.add(String(localMatch.id));
    if (localMatch.clientLocalId) matchedLocalKeys.add(String(localMatch.clientLocalId));

    // 服务端缺挂载、本地已有 → 保留本地 url/签名字段
    if (!imageSpaceItemHasDisplayUrl(serverItem) && imageSpaceItemHasDisplayUrl(localMatch)) {
      merged.push(preferImageSpaceUrlFields({
        ...serverItem,
        url: localMatch.url,
        viewUrl: localMatch.viewUrl || serverItem.viewUrl,
        displayUrl: localMatch.displayUrl || serverItem.displayUrl,
        clientLocalId: localMatch.clientLocalId || serverItem.clientLocalId,
        dataUrl: localMatch.dataUrl,
        ingestPending: localMatch.ingestPending === true,
      }, localMatch));
      continue;
    }
    merged.push(preferImageSpaceUrlFields(serverItem, localMatch));
  }

  const serverIndex = indexImageSpaceItemsForMerge([merged]);
  const pendingById = new Map();
  localLists.forEach((list) => {
    (Array.isArray(list) ? list : []).forEach((item) => {
      if (!item || item.id == null || item.id === '') return;
      const id = String(item.id);
      if (matchedLocalKeys.has(id)) return;
      if (item.clientLocalId && matchedLocalKeys.has(String(item.clientLocalId))) return;
      if (findImageSpaceMergeMatch(item, serverIndex)) return;
      if (pendingById.has(id)) return;
      if (!isLocalUnsyncedImageSpaceItem(item)) return;
      pendingById.set(id, {
        ...item,
        id,
        ingestPending: true,
      });
    });
  });

  if (pendingById.size === 0) {
    return sortImageSpaceRecordsByCreatedAtDesc(merged);
  }
  return sortImageSpaceRecordsByCreatedAtDesc([...merged, ...pendingById.values()]);
}

/**
 * 上传刚结束把 enriched 合入现有列表：三键去重，避免 LIB-temp 与存储 id 双卡。
 * 必须保留 prev 中已有条目；不可把 incoming 当成「完整服务端页」喂给 mergeServer
 * （否则会只剩新上传、旧列表被冲掉，表现为「上传成功但列表空了」）。
 */
function mergeUploadedImageSpaceIntoList(prevImages, enrichedNewImages) {
  return mergeUploadedImageSpaceIntoListWithDelta(prevImages, enrichedNewImages).images;
}

/**
 * T-A18：同三键合入，并返回 total 净增量（乐观卡被替换时可为 0）。
 * @returns {{ images: Array, totalDelta: number }}
 */
function mergeUploadedImageSpaceIntoListWithDelta(prevImages, enrichedNewImages) {
  const prev = Array.isArray(prevImages) ? prevImages : [];
  const incoming = Array.isArray(enrichedNewImages) ? enrichedNewImages : [];
  if (incoming.length === 0) {
    return { images: prev, totalDelta: 0 };
  }
  const incomingIndex = indexImageSpaceItemsForMerge([incoming]);
  const rest = prev.filter((item) => !findImageSpaceMergeMatch(item, incomingIndex));
  const images = sortImageSpaceRecordsByCreatedAtDesc([...incoming, ...rest]);
  return { images, totalDelta: images.length - prev.length };
}

/** 按 id / clientLocalId 从列表移除乐观卡 */
function removeImageSpaceItemsByIds(prevImages, ids) {
  const prev = Array.isArray(prevImages) ? prevImages : [];
  const idSet = new Set((Array.isArray(ids) ? ids : []).map(String).filter(Boolean));
  if (idSet.size === 0) return { images: prev, removedCount: 0 };
  const images = prev.filter((item) => {
    if (!item) return true;
    const id = item.id != null ? String(item.id) : '';
    const localId = item.clientLocalId != null ? String(item.clientLocalId) : '';
    return !idSet.has(id) && !idSet.has(localId);
  });
  return { images, removedCount: prev.length - images.length };
}

export function resolveImageSpaceOwnerId(image) {
  const raw = image?.ownerUserId
    ?? image?.owner_user_id
    ?? image?.ownerId
    ?? image?.owner_id
    ?? image?.createdByUserId
    ?? image?.userId
    ?? image?.user_id
    ?? '';
  return raw != null && raw !== '' ? String(raw) : '';
}

/** 图片空间卡片预览区固定尺寸（px） */
export const IMAGE_SPACE_UPLOAD_SLOT = {
  width: 250,
  height: 300,
};

export { resolveImageSpaceCreatorLabel } from '@/utils/imageSpaceCreatorMeta';

function filterImageSpaceRecords(
  images,
  { name = '', startDate = '', endDate = '', source = '', ownerId = '' } = {},
) {
  const keyword = String(name || '').trim().toLowerCase();
  const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
  const end = endDate ? new Date(`${endDate}T23:59:59`) : null;
  const sourceKey = String(source || '').trim();
  const ownerKey = String(ownerId || '').trim();

  return (images || []).filter((image) => {
    const haystack = `${image.name || ''} ${image.id || ''}`.toLowerCase();
    const nameMatch = !keyword || haystack.includes(keyword);

    let dateMatch = true;
    if (start || end) {
      const imageDate = parseImageSpaceDate(image.createdAt || image.uploadedAt);
      if (!imageDate) {
        dateMatch = false;
      } else if (start && end) {
        dateMatch = imageDate >= start && imageDate <= end;
      } else if (start) {
        dateMatch = imageDate >= start;
      } else if (end) {
        dateMatch = imageDate <= end;
      }
    }

    const sourceMatch = !sourceKey || resolveImageSpaceSourceKey(image) === sourceKey;
    const ownerMatch = !ownerKey || resolveImageSpaceOwnerId(image) === ownerKey;
    return nameMatch && dateMatch && sourceMatch && ownerMatch;
  });
}

const normalizeImageSpaceCacheItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => item && item.id)
    .map((item) => {
      const workspaceMode = resolveImageRecordWorkspace(item);
      return {
        id: String(item.id),
        name: item.name || '',
        createdAt: item.createdAt || '',
        width: Number(item.width) || null,
        height: Number(item.height) || null,
        size: Number(item.size) || null,
        source: item.source || item.sourceType || 'upload',
        sourceType: item.sourceType || '',
        category: item.category || '',
        fissionBatchId: item.fissionBatchId || '',
        taskId: item.taskId || item.sourceTaskId || '',
        batchId: item.batchId || item.materialBatchId || '',
        url: typeof item.url === 'string' ? item.url : '',
        viewUrl: typeof item.viewUrl === 'string' ? item.viewUrl : (typeof item.storedViewUrl === 'string' ? item.storedViewUrl : ''),
        displayUrl: typeof item.displayUrl === 'string'
          ? item.displayUrl
          : (typeof item.viewUrl === 'string'
            ? item.viewUrl
            : (typeof item.storedUrlDisplay === 'string' ? item.storedUrlDisplay : '')),
        storedViewUrl: typeof item.storedViewUrl === 'string' ? item.storedViewUrl : '',
        storedUrlDisplay: typeof item.storedUrlDisplay === 'string' ? item.storedUrlDisplay : '',
        dataUrl: typeof item.dataUrl === 'string' ? item.dataUrl : null,
        favorite: item.favorite === true || item.isFavorite === true,
        deletedAt: (() => {
          const raw = item.deletedAt;
          if (raw == null) return null;
          const text = String(raw).trim();
          if (!text || text === 'null' || text === 'undefined') return null;
          return text;
        })(),
        folderId: item.folderId ? String(item.folderId) : '',
        ownerId: item.ownerId
          ? String(item.ownerId)
          : (item.ownerUserId ? String(item.ownerUserId) : ''),
        ownerUserId: item.ownerUserId
          ? String(item.ownerUserId)
          : (item.ownerId ? String(item.ownerId) : ''),
        ownerName: item.ownerName || '',
        createdBy: item.createdBy || '',
        createdByName: item.createdByName || '',
        workspaceMode,
        captureSpace: workspaceMode,
        teamId: item.teamId != null && item.teamId !== '' ? String(item.teamId) : '',
        teamShared: item.teamShared === true || String(item.teamShared) === 'true',
        teamSharedVia: item.teamSharedVia || '',
        teamSharedFolderId: item.teamSharedFolderId ? String(item.teamSharedFolderId) : '',
        contentHash: item.contentHash || null,
        ingestPending: item.ingestPending === true,
      };
    });
};

const loadImageSpaceCache = (workspaceMode = getIpeWorkspaceMode()) => {
  try {
    const parsed = userLocal.getJson(imageSpaceCacheKey(workspaceMode), null);
    if (!parsed) return [];
    return normalizeImageSpaceCacheItems(parsed);
  } catch (_) {
    return [];
  }
};

const saveImageSpaceCache = (items, workspaceMode = getIpeWorkspaceMode()) => {
  try {
    const slim = normalizeImageSpaceCacheItems(items).map((item) => ({
      ...item,
      dataUrl: null,
    }));
    userLocal.setJson(imageSpaceCacheKey(workspaceMode), slim);
  } catch (_) {
    // ignore cache errors
  }
};

export function clearImageSpaceCacheForLogout() {
  userLocal.dropLegacyKeys(
    imageSpaceCacheKey('personal'),
    imageSpaceCacheKey('team'),
    LEGACY_FUNDING_IMAGE_SPACE_CACHE_KEY,
  );
}

export {
  IMAGE_SPACE_CACHE_KEY_PREFIX,
  imageSpaceCacheKey,
  parseImageSpaceDate,
  sanitizeImageSpaceRecords,
  filterImageSpaceRecords,
  normalizeImageSpaceCacheItems,
  loadImageSpaceCache,
  saveImageSpaceCache,
  IMAGE_SPACE_LOAD_NAV_IDS,
  IMAGE_SPACE_HUB_NAV_IDS,
  isLocalUnsyncedImageSpaceItem,
  mergeServerImageSpaceWithLocalPending,
  mergeUploadedImageSpaceIntoList,
  mergeUploadedImageSpaceIntoListWithDelta,
  removeImageSpaceItemsByIds,
};
